import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Mail, Trash2, Check, Clock, Users, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import FloatingShapes from "@/components/FloatingShapes";
import api from "@/lib/api";

export default function Circle() {
  const [group, setGroup] = useState(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const r = await api.get("/group"); setGroup(r.data); }
    catch { toast.error("Couldn't load your circle"); }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v) return;
    setSending(true);
    try {
      const r = await api.post("/group/invite", { email: v });
      toast.success(r.data.status === "accepted"
        ? "They're in — already a Loom member ✨"
        : "Invite saved. Ask them to sign up with this email.");
      setEmail(""); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send invite");
    } finally { setSending(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/group/invite/${id}`); toast.success("Seat freed"); load(); }
    catch { toast.error("Couldn't remove"); }
  };

  if (!group) {
    return (
      <div className="min-h-screen flex items-center justify-center font-hand text-3xl text-stone-500">
        gathering your circle…
      </div>
    );
  }

  return (
    <div className="relative min-h-screen grain" data-testid="circle-page">
      <FloatingShapes /><Nav />
      <main className="relative z-10 max-w-4xl mx-auto px-6 pt-32 pb-24">
        <div>
          <span className="tag-pill inline-flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-amber-500"/> inner circle</span>
          <h1 className="font-display text-5xl sm:text-6xl mt-4">Your study circle</h1>
          <p className="font-hand text-stone-500 text-2xl mt-2">lend your whole library to up to five people you trust</p>
        </div>

        {!group.is_lifetime ? (
          <motion.div initial={{opacity:0,y:24}} animate={{opacity:1,y:0}}
                      className="sticky-card mt-12 p-14 text-center" data-testid="circle-locked">
            <Crown className="w-12 h-12 text-amber-400 mx-auto"/>
            <div className="font-display text-3xl mt-6">A Lifetime perk</div>
            <p className="font-hand text-stone-500 text-xl mt-2">unlock 5 shared seats forever with the Lifetime plan.</p>
            <Link to="/pricing" className="btn-primary mt-6 inline-flex" data-testid="circle-upgrade-btn">See Lifetime →</Link>
          </motion.div>
        ) : (
          <div className="mt-12 grid gap-8">
            {/* Seats + invite */}
            <motion.section initial={{opacity:0,y:24}} animate={{opacity:1,y:0}}
                            className="sticky-card p-8" data-testid="seats-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 text-stone-700">
                  <Users className="w-5 h-5 text-indigo-500"/>
                  <span className="font-display text-2xl">Seats</span>
                </div>
                <span className="tag-pill" data-testid="seats-counter">{group.seats_used} / {group.seats_total} used</span>
              </div>

              <form onSubmit={invite} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="invite-form">
                <div className="relative flex-1">
                  <Mail className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2"/>
                  <input
                    type="email" required value={email}
                    onChange={(e)=>setEmail(e.target.value)}
                    placeholder="friend@email.com"
                    className="input pl-11"
                    disabled={group.seats_used >= group.seats_total || sending}
                    data-testid="invite-email-input"
                  />
                </div>
                <button type="submit"
                        disabled={group.seats_used >= group.seats_total || sending}
                        className="btn-primary inline-flex items-center justify-center gap-2"
                        data-testid="invite-submit-button">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
                  Invite
                </button>
              </form>
              {group.seats_used >= group.seats_total && (
                <p className="text-sm text-rose-500 mt-3">All seats are used — free one up to invite someone new.</p>
              )}

              {/* Invite list */}
              <div className="mt-7 space-y-3" data-testid="invite-list">
                {group.invites.length === 0 ? (
                  <p className="font-hand text-stone-500 text-xl">no one yet · invite your first study buddy ↑</p>
                ) : group.invites.map((inv) => (
                  <div key={inv.invite_id}
                       className="flex items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-white/70 px-4 py-3"
                       data-testid={`invite-row-${inv.invite_id}`}>
                    <div className="min-w-0">
                      <div className="text-stone-800 truncate">{inv.member_name || inv.email}</div>
                      {inv.member_name && <div className="text-xs text-stone-400 truncate">{inv.email}</div>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {inv.status === "accepted" ? (
                        <span className="text-xs uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid={`invite-status-${inv.invite_id}`}><Check className="w-3 h-3"/> joined</span>
                      ) : (
                        <span className="text-xs uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1" data-testid={`invite-status-${inv.invite_id}`}><Clock className="w-3 h-3"/> pending</span>
                      )}
                      <button onClick={()=>revoke(inv.invite_id)} className="text-stone-400 hover:text-rose-500" data-testid={`revoke-invite-${inv.invite_id}`}>
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>
          </div>
        )}

        {/* Libraries shared with me */}
        {group.member_of?.length > 0 && (
          <motion.section initial={{opacity:0,y:24}} animate={{opacity:1,y:0}}
                          className="sticky-card p-8 mt-8" data-testid="member-of-card">
            <div className="flex items-center gap-2 text-stone-700">
              <Sparkles className="w-5 h-5 text-indigo-500"/>
              <span className="font-display text-2xl">Libraries shared with you</span>
            </div>
            <p className="font-hand text-stone-500 text-xl mt-1">decks from these circles show up in your dashboard</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {group.member_of.map((m) => (
                <span key={m.owner_id} className="tag-pill" data-testid={`member-of-${m.owner_id}`}>{m.owner_name || m.owner_email}</span>
              ))}
            </div>
          </motion.section>
        )}
      </main>
    </div>
  );
}
